import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { WIDGETS, SPAN_CLASS } from "@/components/DashboardWidgets";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GripVertical, Settings2, RotateCcw } from "lucide-react";

const BY_ID = Object.fromEntries(WIDGETS.map((w) => [w.id, w]));
const PERIODS = [
  { v: "0", l: "Todo o período" },
  { v: "30", l: "Últimos 30 dias" },
  { v: "90", l: "Últimos 90 dias" },
  { v: "180", l: "Últimos 180 dias" },
  { v: "365", l: "Últimos 12 meses" },
];

function SortableWidget({ id, span, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition };
  return (
    <div ref={setNodeRef} style={style}
      className={`relative group ${SPAN_CLASS[span]} ${isDragging ? "z-50 opacity-80" : ""}`}
      data-testid={`cockpit-widget-${id}`}>
      <button {...attributes} {...listeners}
        className="absolute right-2 top-2 z-10 p-1.5 rounded-md text-muted-foreground bg-background/80 border border-border opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing transition-opacity duration-150"
        data-testid={`cockpit-drag-${id}`} title="Arrastar para reordenar">
        <GripVertical size={14} />
      </button>
      {children}
    </div>
  );
}

export default function Cockpit360() {
  const { user } = useAuth();
  const [widgets, setWidgets] = useState(null);
  const [stats, setStats] = useState(null);
  const [charts, setCharts] = useState(null);
  const [bi, setBi] = useState(null);
  const [vis, setVis] = useState(null);
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("todos");
  const [days, setDays] = useState("0");
  const saveTimer = useRef(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  useEffect(() => {
    api.get("/dashboard/cockpit-layout").then(({ data }) => setWidgets(data.widgets));
    api.get("/dashboard/visibility").then(({ data }) => setVis(data));
    if (user.role !== "cliente") api.get("/clients").then(({ data }) => setClients(data)).catch(() => {});
  }, [user.role]);

  useEffect(() => {
    const params = {};
    if (clientId !== "todos") params.client_id = clientId;
    if (days !== "0") params.days = days;
    api.get("/dashboard/stats", { params }).then(({ data }) => setStats(data));
    api.get("/dashboard/charts", { params }).then(({ data }) => setCharts(data));
    api.get("/dashboard/bi", { params }).then(({ data }) => setBi(data));
  }, [clientId, days]);

  const save = (ws) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/dashboard/cockpit-layout", { widgets: ws }).catch(() => toast.error("Falha ao salvar o layout"));
    }, 400);
  };

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    setWidgets((ws) => {
      const ids = ws.map((w) => w.id);
      const moved = arrayMove(ws, ids.indexOf(active.id), ids.indexOf(over.id));
      save(moved);
      return moved;
    });
  };

  const toggle = (id) => {
    setWidgets((ws) => {
      const next = ws.map((w) => (w.id === id ? { ...w, visible: !w.visible } : w));
      save(next);
      return next;
    });
  };

  const resetLayout = () => {
    const def = WIDGETS.map((w) => ({ id: w.id, visible: true }));
    setWidgets(def);
    save(def);
    toast.success("Layout restaurado para o padrão");
  };

  if (!widgets || !stats || !charts || !bi || !vis) {
    return <div className="flex items-center justify-center h-64"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;
  }

  const data = { stats, charts, bi };
  const allowed = widgets.filter((w) => BY_ID[w.id] && vis[BY_ID[w.id].vis] !== false && !(w.id === "pga" && !bi.pga));
  const visible = allowed.filter((w) => w.visible);

  return (
    <div className="space-y-6" data-testid="cockpit-page">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Cockpit 360</h1>
          <p className="text-sm text-muted-foreground mt-1">Arraste os widgets para montar o seu painel. O layout é salvo automaticamente para o seu usuário.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {user.role !== "cliente" && (
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="w-44 h-9" data-testid="cockpit-client-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os clientes</SelectItem>
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40 h-9" data-testid="cockpit-period-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PERIODS.map((p) => <SelectItem key={p.v} value={p.v}>{p.l}</SelectItem>)}
            </SelectContent>
          </Select>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" data-testid="cockpit-customize-btn">
                <Settings2 size={15} className="mr-2" /> Personalizar
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72">
              <div className="flex items-center justify-between mb-3">
                <p className="font-heading font-semibold text-sm">Widgets do painel</p>
                <button className="text-xs text-primary hover:underline flex items-center gap-1" onClick={resetLayout} data-testid="cockpit-reset-btn">
                  <RotateCcw size={11} /> Restaurar
                </button>
              </div>
              <div className="space-y-2 max-h-80 overflow-y-auto scrollbar-thin pr-1">
                {allowed.map((w) => (
                  <div key={w.id} className="flex items-center justify-between gap-2">
                    <span className="text-sm">{BY_ID[w.id].label}</span>
                    <Switch checked={w.visible} onCheckedChange={() => toggle(w.id)} data-testid={`cockpit-toggle-${w.id}`} />
                  </div>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={visible.map((w) => w.id)} strategy={rectSortingStrategy}>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {visible.map((w) => {
              const def = BY_ID[w.id];
              const C = def.C;
              return (
                <SortableWidget key={w.id} id={w.id} span={def.span}>
                  <C {...data} filters={{ clientId: clientId !== "todos" ? clientId : null, days: days !== "0" ? days : null }} />
                </SortableWidget>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>
      {visible.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-16">Nenhum widget visível. Use "Personalizar" para ativar widgets.</p>
      )}
    </div>
  );
}
